import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Input } from "@heroui/react";
import { Plus, ChatCircle, Clock, At } from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { useUsers } from 'shared/contexts/users.context';

interface ActionNote {
  id: number;
  message: string;
  userId: string;
  createdAt: string;
  remindMeAt?: string;
  mentionedUsers?: Array<{
    userId: string;
    firstName: string;
    lastName: string;
  }>;
}

interface MentionSuggestion {
  id: string;
  name: string;
  email: string;
}

interface ActionNotesSectionProps {
  ticket: any;
}

export const ActionNotesSection: React.FC<ActionNotesSectionProps> = ({ ticket }) => {
  const [actionNotes, setActionNotes] = useState<ActionNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [newRemindMeAt, setNewRemindMeAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { session } = useSession();
  const { user } = useUser();
  const { users } = useUsers(); // Get all users from context

  // Mention functionality state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionSuggestion[]>([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [taggedUsers, setTaggedUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [createTask, setCreateTask] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const fetchActionNotes = async () => {
    if (!ticket?.id) return;
    
    setIsLoadingNotes(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}/action-notes`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const notes = await response.json();
        setActionNotes(notes);
      }
    } catch (error) {
      console.error('Error fetching action notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Filter users for mention suggestions based on query
  const filterUsers = (query: string) => {
    const lowerQuery = query.toLowerCase();
    const filtered = users
      .filter(u => {
        // Handle different possible property names (firstName vs first_name, etc.)
        const firstName = u.firstName || u.first_name || '';
        const lastName = u.lastName || u.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
        const email = u.emailAddresses?.[0]?.emailAddress?.toLowerCase() || 
                      u.email_addresses?.[0]?.email_address?.toLowerCase() || 
                      u.email?.toLowerCase() || '';
        
        // Match on name or email
        const matches = fullName.includes(lowerQuery) || email.includes(lowerQuery);
        return matches;
      })
      .slice(0, 10) // Limit to 10 results
      .map(u => {
        const firstName = u.firstName || u.first_name || '';
        const lastName = u.lastName || u.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        const email = u.emailAddresses?.[0]?.emailAddress || 
                      u.email_addresses?.[0]?.email_address || 
                      u.email || '';
        
        return {
          id: u.id,
          name: fullName || email.split('@')[0] || 'Unnamed User',
          email: email
        };
      });
    
    setMentionSuggestions(filtered);
  };

  // Handle textarea input changes and detect @ mentions
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    setNewMessage(value);
    
    // Detect @ symbol
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's no space after @
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionStartPos(lastAtIndex);
        setMentionQuery(textAfterAt);
        setShowMentionDropdown(true);
        setSelectedMentionIndex(0);
        
        // Filter users locally
        filterUsers(textAfterAt);
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Handle mention selection
  const selectMention = (suggestion: MentionSuggestion) => {
    if (!textareaRef.current) return;
    
    const beforeMention = newMessage.substring(0, mentionStartPos);
    const afterMention = newMessage.substring(mentionStartPos + mentionQuery.length + 1);
    const newText = `${beforeMention}@${suggestion.name} ${afterMention}`;
    
    setNewMessage(newText);
    setShowMentionDropdown(false);
    setMentionQuery('');
    
    // Add to tagged users if not already present
    if (!taggedUsers.find(u => u.id === suggestion.id)) {
      setTaggedUsers([...taggedUsers, { id: suggestion.id, name: suggestion.name }]);
    }
    
    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = beforeMention.length + suggestion.name.length + 2;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showMentionDropdown) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev => 
        prev < mentionSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
    } else if (e.key === 'Enter' && mentionSuggestions.length > 0) {
      e.preventDefault();
      selectMention(mentionSuggestions[selectedMentionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  // Remove a tagged user
  const removeTaggedUser = (userId: string) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };

  const handleAddNote = async () => {
    if (!newMessage.trim()) {
      toast.error('Voer een bericht in');
      return;
    }

    setIsSubmitting(true);
    try {
      const noteData = {
        message: newMessage.trim(),
        remindMeAt: newRemindMeAt ? new Date(newRemindMeAt).toISOString() : null,
        userId: user?.id,
        mentionedUserIds: taggedUsers.map(u => u.id),
        createTask: createTask && taggedUsers.length > 0
      };

      console.log('📤 Sending action note:', {
        ...noteData,
        taggedUsersCount: taggedUsers.length,
        taggedUsers: taggedUsers,
        willCreateTask: noteData.createTask
      });

      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${ticket.id}/action-notes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(noteData),
        },
        session
      );

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Action note created:', result);
        
        let successMessage = 'Actie notitie toegevoegd';
        if (taggedUsers.length > 0) {
          successMessage = `Actie notitie toegevoegd en ${taggedUsers.length} gebruiker(s) getagd`;
          if (createTask) {
            successMessage += ' (taak aangemaakt)';
          }
        }
        toast.success(successMessage);
        
        // Reset form
        setNewMessage('');
        setNewRemindMeAt('');
        setTaggedUsers([]);
        setCreateTask(false);
        setShowAddNote(false);
        
        fetchActionNotes(); // Refresh the list
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to add action note:', errorText);
        throw new Error("Failed to add action note");
      }
    } catch (error) {
      console.error("Error adding action note:", error);
      toast.error("Error bij toevoegen van actie notitie");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return `Vandaag om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInHours < 48) {
      return `Gisteren om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInHours < 168) { // 7 days
      const dayNames = ['zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'];
      return `${dayNames[date.getDay()]} om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('nl-NL', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getUserImageUrlById = (userId: string) => {
    return `https://img.clerk.com/${userId}`;
  };

  const getUserName = (userId: string) => {
    if (userId === user?.id) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Jij';
    }

    const matchedUser = users.find((u) => u.id === userId);
    if (matchedUser) {
      const firstName = matchedUser.firstName || matchedUser.first_name || '';
      const lastName = matchedUser.lastName || matchedUser.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      if (fullName) return fullName;

      const email =
        matchedUser.emailAddresses?.[0]?.emailAddress ||
        matchedUser.email_addresses?.[0]?.email_address ||
        matchedUser.email ||
        '';
      if (email) return email;
    }

    return 'Gebruiker';
  };

  useEffect(() => {
    if (ticket?.id) {
      fetchActionNotes();
    }
  }, [ticket?.id]);

  return (
    <div className="space-y-4 p-4">
      {/* Add New Note Button */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-neutral-primary">Actie Notities</h3>
        <Button
          onClick={() => setShowAddNote(true)}
          size="sm"
          className="bg-moss-primary text-white hover:bg-moss-primary/90"
          startContent={<Plus size={16} />}
        >
          Notitie toevoegen
        </Button>
      </div>

      {/* Add Note Form */}
      {showAddNote && (
        <div className="bg-neutral-primary/5 p-4 rounded-lg border border-neutral-primary/20">
          <div className="space-y-3">
            {/* Message Input with Mention Support */}
            <div className="relative">
              <label className="block text-sm font-medium text-neutral-primary mb-2">
                Bericht
                <span className="text-xs text-neutral-secondary-mid ml-2">
                  (Typ @ om iemand te taggen)
                </span>
              </label>
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                placeholder="Voer uw actie notitie in... Typ @ om een gebruiker te taggen"
                rows={3}
                className="w-full px-3 py-2 bg-white border border-neutral-primary/20 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-moss-primary focus:border-transparent resize-y text-neutral-primary"
              />
              
              {/* Mention Dropdown */}
              {showMentionDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-neutral-primary/20 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {mentionSuggestions.length > 0 ? (
                    <div className="py-1">
                      {mentionSuggestions.map((suggestion, index) => (
                        <button
                          key={suggestion.id}
                          onClick={() => selectMention(suggestion)}
                          className={`w-full px-4 py-2 text-left hover:bg-moss-primary/10 transition-colors ${
                            index === selectedMentionIndex ? 'bg-moss-primary/10' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <At size={14} className="text-moss-primary" />
                            <div>
                              <div className="text-sm font-medium text-neutral-primary">
                                {suggestion.name}
                              </div>
                              <div className="text-xs text-neutral-secondary-mid">
                                {suggestion.email}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 text-center text-neutral-secondary-mid text-sm">
                      {mentionQuery ? 'Geen gebruikers gevonden' : 'Typ om te zoeken...'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tagged Users Display */}
            {taggedUsers.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-neutral-primary">
                  Getagde gebruikers
                </label>
                <div className="flex flex-wrap gap-2">
                  {taggedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-1 px-3 py-1 bg-moss-primary/10 text-moss-primary border border-moss-primary/30 rounded-full text-sm"
                    >
                      <At size={12} />
                      <span>{user.name}</span>
                      <button
                        onClick={() => removeTaggedUser(user.id)}
                        className="ml-1 text-moss-primary hover:text-red-600 transition-colors"
                        type="button"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create Task Checkbox */}
            {taggedUsers.length > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createTask"
                  checked={createTask}
                  onChange={(e) => setCreateTask(e.target.checked)}
                  className="w-4 h-4 text-moss-primary border-neutral-primary/20 rounded-sm focus:ring-moss-primary focus:ring-2"
                />
                <label htmlFor="createTask" className="text-sm text-neutral-primary cursor-pointer">
                  Maak taak aan voor getagde gebruikers
                  <span className="text-xs text-neutral-secondary-mid ml-2">
                    (Ze ontvangen email + Slack notificatie)
                  </span>
                </label>
              </div>
            )}
            
            <Input
              type="datetime-local"
              label="Herinner me op (optioneel)"
              value={newRemindMeAt}
              onChange={(e) => setNewRemindMeAt(e.target.value)}
              classNames={{
                input: "bg-white",
                inputWrapper: "border border-neutral-primary/20"
              }}
            />
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="light"
                onClick={() => {
                  setShowAddNote(false);
                  setNewMessage('');
                  setNewRemindMeAt('');
                  setTaggedUsers([]);
                  setCreateTask(false);
                }}
                disabled={isSubmitting}
              >
                Annuleren
              </Button>
              <Button
                onClick={handleAddNote}
                isLoading={isSubmitting}
                className="bg-moss-primary text-white hover:bg-moss-primary/90"
              >
                Toevoegen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notes List */}
      {isLoadingNotes ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
          <span className="ml-2 text-neutral-secondary-mid">Notities laden...</span>
        </div>
      ) : actionNotes.length === 0 ? (
        <div className="text-center py-8 text-neutral-secondary-mid">
          <ChatCircle size={48} className="mx-auto mb-2 opacity-50" />
          <p>Geen actie notities gevonden</p>
          <p className="text-sm">Klik op "Notitie toevoegen" om de eerste toe te voegen</p>
        </div>
      ) : (
        <div className="space-y-3">
          {actionNotes.map((note) => (
            <div key={note.id} className="bg-white p-4 rounded-lg border border-neutral-primary/20">
              <div className="flex items-start gap-3">
                <img
                  src={getUserImageUrlById(note.userId)}
                  alt={getUserName(note.userId)}
                  className="w-8 h-8 rounded-full"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(getUserName(note.userId))}&background=31403B&color=F9F0EE`;
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-neutral-primary text-sm">
                      {getUserName(note.userId)}
                    </span>
                    <span className="text-xs text-neutral-secondary-mid">
                      {formatDate(note.createdAt)}
                    </span>
                  </div>
                  <div className="text-sm text-neutral-primary whitespace-pre-wrap">
                    {note.message}
                  </div>
                  
                  {/* Mentioned Users */}
                  {note.mentionedUsers && note.mentionedUsers.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <span className="text-xs text-neutral-secondary-mid">Getagd:</span>
                      {note.mentionedUsers.map((mentionedUser) => (
                        <div
                          key={mentionedUser.userId}
                          className="flex items-center gap-1 px-2 py-0.5 bg-moss-primary/10 text-moss-primary border border-moss-primary/20 rounded-full text-xs"
                        >
                          <At size={10} />
                          <span>{mentionedUser.firstName} {mentionedUser.lastName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {note.remindMeAt && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-brick-primary bg-brick-secondary-fine px-2 py-1 rounded-sm inline-flex">
                      <Clock size={12} />
                      Herinnering: {formatDate(note.remindMeAt)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
